/**
 * 택배사 코드 상수 및 정보
 *
 * 사용처:
 * - farmfree-admin
 * - farmfree-mobile
 * - farmfree-functions
 */

export const COURIER_CODES = {
  LOTTE: 'lotte',
  CJ: 'cj',
  POST: 'post',
  HANJIN: 'hanjin',
  LOGEN: 'logen',
  GSPOSTBOX: 'gspostbox',
  ILYANG: 'ilyang',
} as const

export type CourierCode = (typeof COURIER_CODES)[keyof typeof COURIER_CODES]

/**
 * 인증 방식 타입
 */
export type AuthMethod = '2FA_SMS' | '2FA_EMAIL' | '2FA_KAKAO' | '2FA_OTP'

/**
 * 인증 코드 타입
 */
export type AuthCodeType = 'NUMERIC' | 'ALPHANUMERIC'

/**
 * 2FA 설정 인터페이스
 */
export interface TwoFactorAuthConfig {
  required: boolean
  method?: AuthMethod
  codeLength?: number
  codeType?: AuthCodeType
}

/**
 * 택배사 정보 인터페이스
 */
export interface CourierInfo {
  id: CourierCode
  trackingApiCode: CourierCode
  displayName: string
  icon: string

  // 기능 지원 여부
  supportsTracking: boolean // Tracking API 지원 여부 (공개 조회 크롤링)
  supportsAccount: boolean // Account API 지원 여부 (계정 기반 조회/등록)

  adminUrl: string
  trackingUrl: string
  customerServicePhone: string

  // 인증 관련 (참고용 - 실제 2FA 필요 여부는 authProvider.login() 응답으로 결정됨)
  // ⚠️ requires2FA: false인 경우 → 항상 accessToken 즉시 반환 (롯데)
  // ⚠️ requires2FA: true인 경우 → 상황에 따라 다름 (CJ)
  //    - 새 IP/기기 → REQUIRES_OTP 반환 (SMS 인증 필요)
  //    - 1시간 이내 재로그인 또는 인증된 IP → accessToken 즉시 반환
  requires2FA: boolean // 2FA 가능성 여부 (참고용)
  authMethod?: AuthMethod | null // 인증 방식 (SMS, EMAIL, KAKAO, OTP)
  authCodeLength?: number | null // 인증 코드 길이 (예: 4, 6, 10)
  authCodeType?: AuthCodeType | null // 인증 코드 타입 (NUMERIC, ALPHANUMERIC)

  // 재인증 관련
  supportsAutoReauth: boolean // 자동 재인증 지원 여부 (롯데: true, CJ: false)
  allowsMultipleSessions: boolean // 이중 로그인 허용 여부 (롯데: true, CJ: false)
  reauthMessage: string // 재인증 안내 메시지

  // 토큰 관련
  accessTokenDurationMinutes: number // 토큰 유효 시간 (분)

  // 조회 범위 관련
  maxInquiryDays: number // 한 번에 조회 가능한 최대 날짜 범위 (일 단위)
}

/**
 * 택배사 정보 맵
 */
export const COURIER_INFO_MAP: Record<CourierCode, CourierInfo> = {
  [COURIER_CODES.LOTTE]: {
    id: COURIER_CODES.LOTTE,
    trackingApiCode: COURIER_CODES.LOTTE,
    displayName: '롯데택배',
    icon: '📦',
    // 기능 지원
    supportsTracking: true, // 공개 조회 크롤링 가능
    supportsAccount: true, // 계정 기반 조회/등록 가능
    adminUrl: 'https://partner.alps.llogis.com/',
    trackingUrl: 'https://www.lotteglogis.com/home/reservation/tracking/index',
    customerServicePhone: '1588-2121',
    // 인증 관련
    requires2FA: false,
    authMethod: null,
    authCodeLength: null,
    authCodeType: null,
    // 재인증 관련
    supportsAutoReauth: true, // ID/PW만으로 재인증 가능
    allowsMultipleSessions: true, // 이중 로그인 허용
    reauthMessage: '자동으로 재인증을 시도합니다.',
    // 토큰 관련
    accessTokenDurationMinutes: 1440, // 24시간
    // 조회 범위
    maxInquiryDays: 31, // 최대 31일
  },
  [COURIER_CODES.CJ]: {
    id: COURIER_CODES.CJ,
    trackingApiCode: COURIER_CODES.CJ,
    displayName: 'CJ대한통운',
    icon: '🚚',
    // 기능 지원
    supportsTracking: true, // 공개 조회 크롤링 가능
    supportsAccount: true, // 계정 기반 API 구현 완료 (SMS 2FA 인증)
    adminUrl: 'https://loisparcelp.cjlogistics.com/index.do',
    trackingUrl: 'https://www.cjlogistics.com/ko/tool/parcel/tracking',
    customerServicePhone: '1588-1255',
    // 인증 관련
    requires2FA: true,
    authMethod: '2FA_SMS',
    authCodeLength: 4,
    authCodeType: 'NUMERIC',
    // 재인증 관련
    supportsAutoReauth: false, // SMS 2FA 필요로 자동 재인증 불가
    allowsMultipleSessions: false, // 이중 로그인 불가
    reauthMessage: 'SMS 인증이 필요합니다. 재인증 페이지로 이동합니다.',
    // 토큰 관련
    accessTokenDurationMinutes: 180, // 3시간
    // 조회 범위
    maxInquiryDays: 1, // 하루씩만 조회 가능
  },
  [COURIER_CODES.POST]: {
    id: COURIER_CODES.POST,
    trackingApiCode: COURIER_CODES.POST,
    displayName: '우체국택배',
    icon: '✉️',
    // 기능 지원
    supportsTracking: true, // 공개 조회 크롤링 가능
    supportsAccount: false, // 계정 기반 API 미구현
    adminUrl: 'https://service.epost.go.kr/iservice/',
    trackingUrl: 'https://service.epost.go.kr/iservice/usr/trace/usrtrc001k01.jsp',
    customerServicePhone: '1588-1300',
    // 인증 관련
    requires2FA: false,
    authMethod: null,
    authCodeLength: null,
    authCodeType: null,
    // 재인증 관련
    supportsAutoReauth: true, // 자동 재인증 가능 (추정)
    allowsMultipleSessions: true, // 이중 로그인 허용 (추정)
    reauthMessage: '자동으로 재인증을 시도합니다.',
    // 토큰 관련 (추정치)
    accessTokenDurationMinutes: 720, // 12시간
    // 조회 범위
    maxInquiryDays: 7, // 최대 7일
  },
  [COURIER_CODES.HANJIN]: {
    id: COURIER_CODES.HANJIN,
    trackingApiCode: COURIER_CODES.HANJIN,
    displayName: '한진택배',
    icon: '📮',
    // 기능 지원
    supportsTracking: false, // 미구현
    supportsAccount: false, // 미구현
    adminUrl: 'https://www.hanjin.com',
    trackingUrl: 'https://www.hanjin.com/kor/CMS/DeliveryMgr/WareHouseList.do',
    customerServicePhone: '1588-0011',
    // 인증 관련 (미구현)
    requires2FA: false,
    authMethod: null,
    authCodeLength: null,
    authCodeType: null,
    // 재인증 관련
    supportsAutoReauth: true, // 자동 재인증 가능 (추정)
    allowsMultipleSessions: true, // 이중 로그인 허용 (추정)
    reauthMessage: '자동으로 재인증을 시도합니다.',
    // 토큰 관련 (추정치)
    accessTokenDurationMinutes: 1440, // 24시간
    // 조회 범위
    maxInquiryDays: 7, // 최대 7일 (추정)
  },
  [COURIER_CODES.LOGEN]: {
    id: COURIER_CODES.LOGEN,
    trackingApiCode: COURIER_CODES.LOGEN,
    displayName: '로젠택배',
    icon: '📤',
    // 기능 지원
    supportsTracking: false, // 미구현
    supportsAccount: false, // 미구현
    adminUrl: 'https://www.ilogen.com',
    trackingUrl: 'https://www.ilogen.com/iLOGEN.Web.New/TRACE/TraceSearch.aspx',
    customerServicePhone: '1588-9988',
    // 인증 관련 (미구현)
    requires2FA: false,
    authMethod: null,
    authCodeLength: null,
    authCodeType: null,
    // 재인증 관련
    supportsAutoReauth: true, // 자동 재인증 가능 (추정)
    allowsMultipleSessions: true, // 이중 로그인 허용 (추정)
    reauthMessage: '자동으로 재인증을 시도합니다.',
    // 토큰 관련 (추정치)
    accessTokenDurationMinutes: 1440, // 24시간
    // 조회 범위
    maxInquiryDays: 7, // 최대 7일 (추정)
  },
  [COURIER_CODES.GSPOSTBOX]: {
    id: COURIER_CODES.GSPOSTBOX,
    trackingApiCode: COURIER_CODES.GSPOSTBOX,
    displayName: 'GS편의점택배',
    icon: '🏪',
    // 기능 지원
    supportsTracking: false, // 미구현
    supportsAccount: false, // 미구현
    adminUrl: 'https://www.gspostbox.com',
    trackingUrl: 'https://www.gspostbox.com',
    customerServicePhone: '1577-1287',
    // 인증 관련 (미구현)
    requires2FA: false,
    authMethod: null,
    authCodeLength: null,
    authCodeType: null,
    // 재인증 관련
    supportsAutoReauth: true, // 자동 재인증 가능 (추정)
    allowsMultipleSessions: true, // 이중 로그인 허용 (추정)
    reauthMessage: '자동으로 재인증을 시도합니다.',
    // 토큰 관련 (추정치)
    accessTokenDurationMinutes: 1440, // 24시간
    // 조회 범위
    maxInquiryDays: 7, // 최대 7일 (추정)
  },
  [COURIER_CODES.ILYANG]: {
    id: COURIER_CODES.ILYANG,
    trackingApiCode: COURIER_CODES.ILYANG,
    displayName: '일양로지스',
    icon: '📬',
    // 기능 지원
    supportsTracking: false, // 미구현
    supportsAccount: false, // 미구현
    adminUrl: 'https://www.ilyanglogis.com',
    trackingUrl: 'https://www.ilyanglogis.com/tracking',
    customerServicePhone: '1588-0002',
    // 인증 관련 (미구현)
    requires2FA: false,
    authMethod: null,
    authCodeLength: null,
    authCodeType: null,
    // 재인증 관련
    supportsAutoReauth: true, // 자동 재인증 가능 (추정)
    allowsMultipleSessions: true, // 이중 로그인 허용 (추정)
    reauthMessage: '자동으로 재인증을 시도합니다.',
    // 토큰 관련 (추정치)
    accessTokenDurationMinutes: 1440, // 24시간
    // 조회 범위
    maxInquiryDays: 7, // 최대 7일 (추정)
  },
}

/**
 * Tracking API 지원 택배사 목록 가져오기 (공개 조회 크롤링)
 */
export function getTrackingEnabledCouriers() {
  return Object.values(COURIER_INFO_MAP).filter((courier) => courier.supportsTracking)
}

/**
 * Account API 지원 택배사 목록 가져오기 (계정 기반 조회/등록)
 */
export function getAccountEnabledCouriers() {
  return Object.values(COURIER_INFO_MAP).filter((courier) => courier.supportsAccount)
}

/**
 * 활성화된 택배사 목록 가져오기 (하위 호환성)
 * @deprecated supportsTracking 또는 supportsAccount 중 하나라도 true인 택배사 반환
 */
export function getActiveCouriers() {
  return Object.values(COURIER_INFO_MAP).filter(
    (courier) => courier.supportsTracking || courier.supportsAccount
  )
}

/**
 * 택배사 코드로 정보 가져오기
 */
export function getCourierInfo(code: string) {
  return COURIER_INFO_MAP[code as CourierCode]
}

/**
 * 택배사의 토큰 만료 시간 계산
 * @param courierCode 택배사 코드
 * @returns Unix timestamp (밀리초)
 */
export function getTokenExpirationTime(courierCode: CourierCode): number {
  const info: CourierInfo = getCourierInfo(courierCode)
  return Date.now() + info.accessTokenDurationMinutes * 60 * 1000
}

/**
 * 택배사의 2FA 설정 가져오기
 * @param courierCode 택배사 코드
 * @returns 2FA 설정 정보
 */
export function get2FAConfig(courierCode: CourierCode): TwoFactorAuthConfig {
  const info: CourierInfo = getCourierInfo(courierCode)
  return {
    required: info.requires2FA,
    method: info.authMethod || undefined,
    codeLength: info.authCodeLength || undefined,
    codeType: info.authCodeType || undefined,
  }
}

/**
 * CJ 파일 상태 코드 매핑
 */
export const CJ_FILE_STATUS_MAP: Record<string, string> = {
  '00': '접수대기',
  '01': '접수진행',
  '02': '접수완료',
  '03': '출력완료',
  '04': '발송완료',
  '05': '접수오류',
  '99': '접수취소',
}

/**
 * CJ 파일 상태 코드를 한글로 변환
 * @param statusCode CJ 파일 상태 코드
 * @returns 한글 상태명
 */
export function getCJFileStatusText(statusCode: string): string {
  return CJ_FILE_STATUS_MAP[statusCode] || statusCode
}
